import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import QuoteSummary from '../components/QuoteSummary';
import { getDirectJobModerationQueue, approveDirectJob, rejectDirectJob } from '../api/directJob';
import { DirectJob } from '../types/directJob';

const AdminDirectJobsPage: React.FC = () => {
  const navigate = useNavigate();
  const [directJobs, setDirectJobs] = useState<DirectJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    getDirectJobModerationQueue()
      .then(setDirectJobs)
      .catch(() => setError('Failed to load Direct Job queue.'))
      .finally(() => setLoading(false));
  }, []);

  const handleApprove = async (id: number) => {
    setActionInProgress(id);
    try {
      await approveDirectJob(id);
      setDirectJobs(prev => prev.filter(dj => dj.id !== id));
    } catch {
      setError('Failed to approve Direct Job.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (id: number) => {
    if (!rejectReason.trim()) return;
    setActionInProgress(id);
    try {
      await rejectDirectJob(id, rejectReason.trim());
      setDirectJobs(prev => prev.filter(dj => dj.id !== id));
    } catch {
      setError('Failed to reject Direct Job.');
    } finally {
      setActionInProgress(null);
      setRejecting(null);
      setRejectReason('');
    }
  };

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin/jobs')}
          className="flex items-center gap-1 text-secondary font-label-bold mb-6 hover:underline"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Job Review Queue
        </button>

        <h1 className="text-3xl font-bold text-on-surface mb-2">Direct Job Review Queue</h1>
        <p className="text-on-surface-variant mb-8">Contractor-initiated jobs and quotes awaiting moderation before reaching their Client.</p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-error-container text-on-error-container text-sm">{error}</div>
        )}

        {loading ? (
          <p className="text-on-surface-variant">Loading...</p>
        ) : directJobs.length === 0 ? (
          <div className="text-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined text-5xl mb-3 block">check_circle</span>
            <p className="font-medium">Queue is clear</p>
            <p className="text-sm mt-1">No Direct Jobs are waiting for review.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {directJobs.map(dj => (
              <div key={dj.id} className="bg-surface rounded-2xl border border-outline-variant p-5 flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold text-on-surface text-lg truncate">{dj.title}</h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-on-surface-variant">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">business_center</span>
                        {dj.contractorCompanyName}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">mail</span>
                        {dj.recipientEmail}
                      </span>
                      {dj.townName && (
                        <span className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">location_on</span>
                          {dj.townName}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">schedule</span>
                        {new Date(dj.createdAt).toLocaleDateString('en-ZA')}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-on-surface line-clamp-3">{dj.description}</p>

                {dj.quote && <QuoteSummary quote={dj.quote} />}

                {rejecting === dj.id ? (
                  <div className="p-3 rounded-lg bg-error-container text-on-error-container text-sm space-y-2">
                    <p className="font-medium">Reason for rejection (required, shown to the contractor):</p>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      className="w-full p-2 rounded-lg border border-outline-variant bg-surface text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleReject(dj.id)}
                        disabled={actionInProgress === dj.id || !rejectReason.trim()}
                        className="px-3 py-1.5 rounded-lg bg-error text-on-error font-medium text-xs disabled:opacity-50"
                      >
                        {actionInProgress === dj.id ? 'Rejecting...' : 'Confirm reject'}
                      </button>
                      <button
                        onClick={() => { setRejecting(null); setRejectReason(''); }}
                        className="px-3 py-1.5 rounded-lg border border-outline-variant text-xs font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(dj.id)}
                      disabled={actionInProgress === dj.id}
                      className="flex-1 bg-primary text-on-primary py-2 rounded-lg font-label-bold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {actionInProgress === dj.id ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setRejecting(dj.id)}
                      disabled={actionInProgress === dj.id}
                      className="px-4 py-2 rounded-lg border border-error text-error font-label-bold text-sm hover:bg-error/10 transition-colors disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      <BottomNavBar />
    </div>
  );
};

export default AdminDirectJobsPage;
